import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';

import { nameSchema } from 'utils/validators';

import {
  confirmReviewAssetUpload,
  getReviewForm,
  requestReviewAssetUpload,
  submitGroupReview,
  submitReview,
} from './api';
import { buildRatings } from './utils/forms';

import { ReviewScene } from './scene';
import { SHARE_PROMPT_MIN_RATING } from './scene/constants';
import { buildProfileUrl, providerDisplayName } from './scene/helpers';

const MAX_REVIEW_MEDIA = 5;

const reviewFormSchema = z.object({
  reviewer_name: nameSchema,
  comment: z.string().max(2000, 'Too long'),
  improvement_suggestion: z.string().max(2000, 'Too long'),
  improvement_public: z.boolean(),
  contactable: z.boolean(),
  timeframe: z.string().optional(),
}) satisfies z.ZodType<Review.ReviewFormValues>;

const computeOverall = (categoryRatings: Record<string, number>): number => {
  const values = Object.values(categoryRatings).filter((rating) => rating > 0);
  if (values.length === 0) return 0;
  const sum = values.reduce((accumulator, rating) => accumulator + rating, 0);
  return Math.round(sum / values.length);
};

export default function Review() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Kudable.ReviewFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [categoryRatings, setCategoryRatings] = useState<
    Record<string, number>
  >({});
  const [uploadedAssets, setUploadedAssets] = useState<
    Review.UploadedReviewAsset[]
  >([]);
  const [participantRatings, setParticipantRatings] = useState<
    Record<string, number>
  >({});
  const [participantComments, setParticipantComments] = useState<
    Record<string, string>
  >({});
  const [shareModalState, setShareModalState] =
    useState<Review.ShareModalState | null>(null);

  const overallRating = useMemo(
    () => computeOverall(categoryRatings),
    [categoryRatings],
  );

  const form = useForm<Review.ReviewFormValues>({
    resolver: zodResolver(reviewFormSchema),
    mode: 'onBlur',
    defaultValues: {
      reviewer_name: '',
      comment: '',
      improvement_suggestion: '',
      improvement_public: false,
      contactable: false,
      timeframe: '',
    },
  });

  useEffect(() => {
    getReviewForm(token!)
      .then((response) => {
        const data = response.data;
        setFormData(data);
        form.reset({
          reviewer_name: data.link.client_name ?? '',
          comment: '',
          improvement_suggestion: '',
          improvement_public: false,
          contactable: false,
          timeframe: '',
        });
      })
      .catch((requestError: AxiosError) => {
        setError(
          requestError.response?.status === 410
            ? 'This review link has already been used.'
            : 'This link is invalid or has expired.',
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCategoryChange = (category: string, rating: number) => {
    setCategoryRatings((previousRatings) => ({
      ...previousRatings,
      [category]: rating,
    }));
  };

  const handleAddMedia = async (file: File) => {
    if (uploadedAssets.length >= MAX_REVIEW_MEDIA) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const uploadRes = await requestReviewAssetUpload(token!, {
        content_type: file.type,
        size_bytes: file.size,
      });
      const { asset, upload_url } = uploadRes.data;

      const putRes = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) throw new Error('Upload failed. Please try again.');

      await confirmReviewAssetUpload(token!, asset.id);

      setUploadedAssets((previousAssets) => [
        ...previousAssets,
        { id: asset.id, kind: asset.kind, url: asset.url },
      ]);
    } catch (uploadException: any) {
      setUploadError(
        uploadException.message ?? 'Upload failed. Please try again.',
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveMedia = (id: string) => {
    setUploadedAssets((previousAssets) =>
      previousAssets.filter((asset) => asset.id !== id),
    );
  };

  const handleSubmit = (values: Review.ReviewFormValues) => {
    if (!formData) return;
    const trimmedSuggestion = values.improvement_suggestion.trim();
    const isCreator = (formData.provider as any).provider_mode === 'creator';

    const link = formData.link;
    const phone = link.client_phone ?? undefined;
    const email = link.client_email ?? undefined;

    if (formData.link.is_group) {
      const allRated = formData.link.participants.every(
        (participant) => (participantRatings[participant.member_id] ?? 0) > 0,
      );
      if (!allRated) {
        setError('Please rate every team member.');
        return;
      }

      return submitGroupReview(token!, {
        reviewer_name: values.reviewer_name,
        overall_comment: values.comment,
        reviewer_phone: phone,
        reviewer_email: email,
        participant_ratings: formData.link.participants.map((participant) => ({
          member_id: participant.member_id,
          rating: participantRatings[participant.member_id] || 0,
          comment: participantComments[participant.member_id] || undefined,
        })),
        asset_ids: uploadedAssets.map((asset) => asset.id),
        improvement_suggestion: trimmedSuggestion || undefined,
        improvement_public: trimmedSuggestion
          ? values.improvement_public
          : undefined,
        contactable: values.contactable || undefined,
      })
        .then(() => {
          const profileToken = formData.provider.profile_token;
          if (profileToken) navigate(`/p/${profileToken}?reviewed=1`);
          else setSubmitted(true);
        })
        .catch(() => setError('Failed to submit. Please try again.'));
    }

    if (overallRating === 0) {
      setError('Tap to rate at least one area.');
      return;
    }

    const result = values.comment.trim();

    return submitReview(token!, {
      overall_rating: overallRating,
      reviewer_name: values.reviewer_name,
      comment: result,
      reviewer_phone: phone,
      reviewer_email: email,
      ratings: buildRatings(formData.categories, categoryRatings),
      asset_ids: uploadedAssets.map((asset) => asset.id),
      improvement_suggestion: trimmedSuggestion || undefined,
      improvement_public: trimmedSuggestion
        ? values.improvement_public
        : undefined,
      contactable: values.contactable || undefined,
      achievement: isCreator ? result || undefined : undefined,
      timeframe: values.timeframe || undefined,
    } as any)
      .then(() => {
        const profileToken = formData.provider.profile_token;
        if (
          overallRating >= SHARE_PROMPT_MIN_RATING &&
          profileToken &&
          !isCreator
        ) {
          setShareModalState({
            providerName: providerDisplayName(formData.provider),
            profileUrl: buildProfileUrl(profileToken),
          });
          return;
        }
        if (profileToken) navigate(`/p/${profileToken}?reviewed=1`);
        else setSubmitted(true);
      })
      .catch(() => setError('Failed to submit. Please try again.'));
  };

  const handleShareModalClose = () => {
    if (!shareModalState) return;
    setShareModalState(null);
    const profileToken = formData?.provider?.profile_token;
    if (profileToken) navigate(`/p/${profileToken}?reviewed=1`);
    else setSubmitted(true);
  };

  const handleParticipantRatingChange = (memberId: string, rating: number) => {
    setParticipantRatings((previousRatings) => ({
      ...previousRatings,
      [memberId]: rating,
    }));
  };

  const handleParticipantCommentChange = (memberId: string, value: string) => {
    setParticipantComments((previousComments) => ({
      ...previousComments,
      [memberId]: value,
    }));
  };

  return (
    <ReviewScene
      form={form}
      onSubmit={handleSubmit}
      formData={formData}
      error={error}
      submitted={submitted}
      overallRating={overallRating}
      categoryRatings={categoryRatings}
      isUploading={isUploading}
      uploadError={uploadError}
      uploadedAssets={uploadedAssets}
      maxMedia={MAX_REVIEW_MEDIA}
      onCategoryChange={handleCategoryChange}
      onAddMedia={handleAddMedia}
      onRemoveMedia={handleRemoveMedia}
      participantRatings={participantRatings}
      participantComments={participantComments}
      onParticipantRatingChange={handleParticipantRatingChange}
      onParticipantCommentChange={handleParticipantCommentChange}
      shareModalState={shareModalState}
      onShareModalClose={handleShareModalClose}
    />
  );
}
